<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { reactive, ref, onMounted, computed, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import LeftMenuWrapper from '@/Components/LeftMenu/LeftMenuWrapper.vue';
import ProductPageLayout from '@/Layouts/ProductPageLayout.vue';
import PriceGraph from '@/Components/Product/PriceGraph.vue';
import SidebarRelaterede from '@/Components/SidebarRelaterede.vue';
import SeOgsaa from '@/Components/SeOgsaa.vue';
import Komponentbaand from '@/Components/PcBygger/Komponentbaand.vue';

import PriceAlertButton from '@/Components/PriceAlert/PriceAlertButton.vue';
import PriceAlertModal from '@/Components/PriceAlert/PriceAlertModal.vue';
import FavoriteButton from '@/Components/Favorite/FavoriteButton.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import AddToListButton from '@/Components/ProductList/AddToListButton.vue';
import PriceRow from '@/Components/Product/PriceRow.vue';
import { sanitizeWithBreaks } from '@/Utils/sanitize';

const page = usePage();

/*
 * Prisudviklingen som tekst. Beregnet på serveren ud fra
 * product_price_histories, så den står i det HTML Google og
 * sprogmodellerne får, ikke først efter grafen er hentet.
 */
const prisudvikling = computed(() => page.props.prisudvikling || null);

// Interne links videre. Server-renderede, så de tæller i link-grafen.
/*
 * Har nogen butik varen PÅ LAGER?
 *
 * Uden det tjek skrev siden "0,00 kr." som overskrift på de 51.565
 * produkter der ikke er til salg lige nu.
 *
 * Tjekket spurgte om antallet af forhandlere, men overskriften viser
 * `lowest_price_in_stock_including_shipping`. De to ting er ikke det samme: en
 * vare kan have tre forhandlere der alle har den udsolgt, og så er tælleren
 * tre mens prisen er nul. Målt 16. oktober 2026 stod 22.775 varer med
 * forhandlere og ingen pris på lager, og den mest besøgte af dem
 * (msi-rtx-4060-ti-gaming-x-16g-4490, popularitet 228) viste live
 * `<h3 class="text-primary">0,00 kr.</h3>`.
 *
 * Det tal vokser med lagerrettelsen i JsonLdExtractor: da vi begyndte at læse
 * "Discontinued" og "BackOrder" rigtigt, holdt en masse annoncer op med at
 * tælle som på lager. Derfor skal de to ting rettes samtidig.
 */
const harPriser = computed(
    () => Number(page.props.product?.prices?.lowest_price_in_stock_including_shipping || 0) > 0,
);

/*
 * Butikkerne har varen, men ingen af dem på lager. Så er "Ingen aktuelle
 * priser" rigtigt om lageret og forkert om siden: vi KENDER en pris, den kan
 * bare ikke købes lige nu. Den vises som sidst sete pris i stedet for at
 * blive skjult.
 */
const harUdsolgtePriser = computed(
    () => !harPriser.value && Number(page.props.product?.prices?.lowest_price_including_shipping || 0) > 0,
);

const nyheder = computed(() => page.props.nyheder || []);
const relateredeMedBillede = computed(() => page.props.relateredeMedBillede || []);
const seOgsaa = computed(() => page.props.seOgsaa || []);
/*
 * Er varen en del i PC-byggeren? null paa alt andet, og saa findes
 * sektionen ikke. Se Komponentbaand.php.
 */
const komponentbaand = computed(() => page.props.komponentbaand || null);

/*
 * Billeder der ikke kan hentes, fjernes naar browseren siger til.
 *
 * Scraperen samlede 43.650 betalingslogoer og sidepynt op som
 * "produktbilleder", og de fleste af dem kan ikke engang hentes. Oprydningen
 * og et strammere filter i scraperen retter kilden, men en raekke der peger
 * paa en fil der ikke findes, maa ikke efterlade et brudt-billede-ikon i
 * striben imens.
 */
const doedeBilleder = ref(new Set());
const markerDoedt = (id) => { doedeBilleder.value = new Set(doedeBilleder.value).add(id); };

/*
 * Butikkens billede bruges kun naar vi ikke selv har et, og controlleren
 * sender det kun med i det tilfaelde. Kan det ikke hentes, falder vi tilbage
 * til pladsholderen frem for at efterlade et brudt ikon.
 */
const butiksbillede = computed(() => page.props.butiksbillede || null);
const butiksbilledeDoedt = ref(false);

const billeder = computed(
    () => (page.props.product?.images || []).filter((b) => !doedeBilleder.value.has(b.id))
);

/* Hvor mange miniaturer der vises ad gangen. */
const STRIBE = 5;
const stribeStart = ref(0);

const synligeMiniaturer = computed(() => billeder.value.slice(stribeStart.value, stribeStart.value + STRIBE));

/*
 * Hvilket billede der vises stort.
 *
 * Nulstilles naar varen skifter. Inertia genbruger komponenten mellem to
 * produktsider, saa uden det her ville en vare med tre billeder arve
 * indeks 2 fra den forrige og falde ud af sit eget array.
 */
const valgtBillede = ref(0);

watch(() => page.props.product?.id, () => {
    valgtBillede.value = 0;
    stribeStart.value = 0;
    doedeBilleder.value = new Set();
    // Inertia genbruger komponenten mellem to produktsider. Uden det her
    // ville en vare arve "billedet kunne ikke hentes" fra den forrige og
    // vise pladsholderen selvom dens eget billede er fint.
    butiksbilledeDoedt.value = false;
});

/*
 * Vinduet foelger det valgte billede.
 *
 * Uden det her kunne man vaelge billede 3, trykke frem to gange, og det valgte
 * var ude af syne uden nogen maade at se hvor man var.
 */
watch(valgtBillede, (i) => {
    if (i < stribeStart.value) stribeStart.value = i;
    if (i >= stribeStart.value + STRIBE) stribeStart.value = Math.max(0, i - STRIBE + 1);
});

/*
 * Falder det valgte billede bort — fordi det ikke kunne hentes — skal
 * visningen ikke pege ud over listen.
 */
watch(() => billeder.value.length, (n) => {
    if (valgtBillede.value >= n) valgtBillede.value = Math.max(0, n - 1);
    if (stribeStart.value > Math.max(0, n - STRIBE)) stribeStart.value = Math.max(0, n - STRIBE);
});
const brandImageFailed = ref(false);

const user = computed(
    () => page.props.auth.user,
);

const isAuthenticated = computed(
    () => page.props.isAuthenticated,
);

const userPriceAlert = computed(
    () => page.props.userPriceAlert,
);

const userFavoriteStatus = computed(
    () => page.props.userFavoriteStatus || false,
);

const productdata = computed(
    () => page.props.product,
);

/**
 * Det navn brugeren skal se, i samme rangordning som Product::visningsnavnFra()
 * på serveren. Står de to ikke ens, skriver h1 ét navn og fanebladet et andet.
 *
 * For 71,6 procent af de varer der har priser er model_short_name mærke plus
 * varenummer: "ASUS 90MB1CW0-M0EAY0". Det er ikke et navn nogen genkender.
 */
/**
 * Den værdi brugeren skal læse for en specifikation.
 *
 * value_human_displayable bærer tallet gennem den parrede formatter, altså
 * "250 GB" frem for 268435456000. Findes den ikke, falder vi tilbage til den
 * rå værdi.
 */
function specVaerdi(spec) {
    const v = spec?.value_human_displayable ?? spec?.value;

    return Array.isArray(v) ? v.join(', ') : v;
}

const visningsnavn = computed(() => {
    const p = productdata.value || {};

    for (const felt of ['custom_h1_header', 'display_name', 'model_long_name', 'model_short_name']) {
        const navn = (p[felt] || '').trim();
        if (navn) return navn;
    }

    return '';
});

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
    menuItems: Array,
});

function numberFormat(value) {
    //convert to num first
    value = Number(value);

    //use intl
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

const pageTitle = computed(() => {
    return page.props.pageTitle;
});

const breadcrumbs = computed(
    () => page.props.breadcrumbs
);

const urlForCheapestStore = computed(() => {
    return page.props.productPrices?.[0]?.price_guid;
});

const pageUrl = computed(() => {
    return typeof window !== 'undefined' ? window.location.href : page.props.ziggy?.location ?? '';
});

const firstImage = computed(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : (page.props.ziggy?.url ?? '');
    return origin + "/images/" + page.props.product?.images?.[0]?.id;
});

const chartOptions = {
  chart: {
    type: 'line'
  },
  title: {
    text: 'Monthly Sales Data'
  },
  xAxis: {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  },
  yAxis: {
    title: {
      text: 'Revenue (in USD)'
    }
  },
  series: [
    {
      name: 'Sales',
      data: [29.9, 71.5, 106.4, 129.2, 144.0, 176.0, 135.6, 148.5, 216.4, 194.1, 95.6, 54.4]
    }
  ]
}

const showAlertModal = reactive({
    value: false
});

function openPriceAlertModal() {
    showAlertModal.value = true;
}

function handleFavoriteChanged(newStatus) {
    if (typeof document === 'undefined') return;
    const favoriteBtn = document.querySelector('.btn-favorite');
    if (favoriteBtn) {
        favoriteBtn.classList.add('favorite-animation');
        setTimeout(() => {
            favoriteBtn.classList.remove('favorite-animation');
        }, 700); // Remove class after animation completes
    }
}

// Lightbox (image gallery) + its jQuery dependency are ~130 KB and used ONLY on
// this product-detail page. Load them lazily on mount (client-only) so they no
// longer ship in the global eager bundle. jQuery must be a global before
// lightbox2's module runs, so set it first.
onMounted(async () => {
    const jq = (await import('jquery')).default;
    window.$ = window.jQuery = jq;
    await import('lightbox2/dist/css/lightbox.min.css');
    await import('lightbox2/dist/js/lightbox.min.js');
});
</script>

<template>
    <ProductPageLayout>
        <div class="container-fluid product-detail-wrapper">
            <BreadcrumbBar :breadcrumbs="breadcrumbs" />
            <div class="row p-0">
                <div class="col-sm-12 col-md-10">
                    <div class="container-fluid">
                        <div class="row design-row">
                            <div class="col-md-3">
                                <div class="card">
                                    <div class="card-body">
                                        <!--
                                            Hovedbillede + miniaturestribe.

                                            Var en halvbygget Bootstrap-carousel:
                                              :class="{ 'carousel-item': index == 0,
                                                        'carousel-item': index > 0 }"
                                            Samme nøgle to gange, så den anden overskrev den
                                            første. Klassen blev sat på alle UNDTAGEN det
                                            første billede, og .carousel-item er display:none
                                            uden .active. Der var hverken carousel-wrapper,
                                            active-klasse eller knapper, og jQuery-initieringen
                                            skete aldrig.

                                            Resultatet: billede nul var synligt fordi det ved
                                            et uheld ikke fik klassen, og resten var skjult.
                                            135.314 varer har mere end ét billede, og ingen af
                                            dem kunne vise nummer to.

                                            Ingen carousel her. En Vue-tilstand er mindre kode,
                                            kræver ingen initiering, og virker også hvis
                                            jQuery aldrig når frem.
                                        -->
                                        <template v-if="billeder.length > 0">
                                            <a
                                                :href="`/images/${billeder[valgtBillede].id}`"
                                                data-lightbox="product-images"
                                                :data-title="billeder[valgtBillede].name"
                                            >
                                                <span class="galleri-hoved">
                                                    <picture>
                                                        <source :srcset="`/images/${billeder[valgtBillede].id}?w=640&fmt=webp`" type="image/webp" />
                                                        <!--
                                                            Ingen width/height-attributter.

                                                            Der stod 640x640, og det var ikke
                                                            sandt: billederne har alle mulige
                                                            formater. Browseren udleder sit
                                                            aspect-ratio af netop de to tal, saa
                                                            et 800x600-foto blev presset ned i en
                                                            kvadratisk kasse og strakt.

                                                            Kassen er kvadratisk i CSS i stedet,
                                                            og billedet faar object-fit: contain.
                                                            Saa er pladsen reserveret — ingen
                                                            layout-hop — uden at nogen lyver om
                                                            billedets form.
                                                        -->
                                                        <img
                                                            :src="`/images/${billeder[valgtBillede].id}?w=640`"
                                                            :alt="billeder[valgtBillede].name"
                                                            loading="eager"
                                                            fetchpriority="high"
                                                            decoding="async"
                                                            @error="markerDoedt(billeder[valgtBillede].id)"
                                                        >
                                                    </picture>
                                                </span>
                                            </a>

                                            <!--
                                                Miniaturestribe med et vindue paa fem.

                                                Foerste forsoeg viste ALLE billeder paa en
                                                gang. Raspberry Pi-siden har 24, og det blev
                                                fire raekker smaa felter under hovedbilledet.
                                                Fem ad gangen med pile er det man kender fra
                                                andre gallerier, og det holder hoejden fast
                                                uanset om varen har to eller tredive billeder.
                                            -->
                                            <div v-if="billeder.length > 1" class="galleri-strib mt-2">
                                                <button
                                                    v-if="billeder.length > STRIBE"
                                                    type="button"
                                                    class="galleri-pil"
                                                    :disabled="stribeStart === 0"
                                                    aria-label="Vis forrige billeder"
                                                    @click="stribeStart = Math.max(0, stribeStart - STRIBE)"
                                                >
                                                    <i class="bi bi-chevron-left" aria-hidden="true"></i>
                                                </button>

                                                <button
                                                    v-for="image in synligeMiniaturer"
                                                    :key="image.id"
                                                    type="button"
                                                    class="galleri-knap"
                                                    :class="{ valgt: image.id === billeder[valgtBillede].id }"
                                                    :aria-label="`Vis billede ${billeder.indexOf(image) + 1} af ${billeder.length}`"
                                                    :aria-current="image.id === billeder[valgtBillede].id"
                                                    @click="valgtBillede = billeder.indexOf(image)"
                                                >
                                                    <picture>
                                                        <source :srcset="`/images/${image.id}?w=96&fmt=webp`" type="image/webp" />
                                                        <img
                                                            :src="`/images/${image.id}?w=96`"
                                                            :alt="image.name"
                                                            loading="lazy"
                                                            decoding="async"
                                                            @error="markerDoedt(image.id)"
                                                        >
                                                    </picture>
                                                </button>

                                                <button
                                                    v-if="billeder.length > STRIBE"
                                                    type="button"
                                                    class="galleri-pil"
                                                    :disabled="stribeStart + STRIBE >= billeder.length"
                                                    aria-label="Vis naeste billeder"
                                                    @click="stribeStart = Math.min(billeder.length - STRIBE, stribeStart + STRIBE)"
                                                >
                                                    <i class="bi bi-chevron-right" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        </template>
                                        <!--
                                            Butikkens billede, naar vi ikke selv har et.

                                            Adressen har hele tiden ligget i feedet og blev
                                            allerede brugt som og:image, saa varen havde et
                                            rigtigt billede naar siden blev delt, mens selve
                                            siden viste en pladsholder. Det ramte 18.420
                                            produktsider.

                                            Hentes direkte fra butikkens adresse. Ingen
                                            lightbox og ingen miniaturestribe: der er kun det
                                            ene billede, og det ligger ikke i vores egen
                                            /images/-rute som kan skalere det.

                                            INGEN referrerpolicy="no-referrer" her. Den stod
                                            der i foerste udgave, og Proshop — som leverer
                                            billedet til 15.751 af de 17.098 varer det her
                                            handler om — svarer 403 uden en Referer og 200
                                            med. Maalt 21. september 2026; user agent er
                                            ligegyldig, det er alene refereren. Uden den faldt
                                            alle Proshop-varer tilbage til pladsholderen,
                                            OG stod index,follow imens.
                                        -->
                                        <template v-else-if="butiksbillede && !butiksbilledeDoedt">
                                            <span class="galleri-hoved">
                                                <img
                                                    :src="butiksbillede"
                                                    :alt="visningsnavn"
                                                    loading="eager"
                                                    decoding="async"
                                                    @error="butiksbilledeDoedt = true"
                                                >
                                            </span>
                                        </template>
                                        <template v-else>
                                            <div class="carousel-item active">
                                                <img :src="`/images/static/placeholder.png`" class="d-block w-100" alt="Placeholder">
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-9">
                                <div class="card shadow-sm">
                                    <div class="card-body">
                                        <div class="modern-product-card p-4">
                                            <!--
                                                Mærket ligger UDEN FOR h1'en.

                                                Det stod indeni, og for de mærker der ikke har et logo
                                                faldt det tilbage til navnet som ren tekst. Så læste
                                                Google h1'en som
                                                "Samsung Galaxy A25 5G 256GB/8GB - SmartphoneSamsung",
                                                med mærket klistret på uden mellemrum. 2.187 mærker
                                                mangler et logo, så det ramte bredt. Produktsiderne er
                                                den sidetype med flest visninger, og 3.128 af dem
                                                ligger på position 5-20, hvor overskriften betyder
                                                noget.

                                                Visuelt står det uændret: rækken er stadig flex med
                                                mærket til højre.
                                            -->
                                            <div class="product-title-row mb-3 d-flex justify-content-between align-items-center">
                                                <h1 class="product-title h4 mb-0">{{ visningsnavn }}</h1>
                                                <Link :href="route('brands.show', page.props.product.brand.slug)">
                                                    <!-- Stod som /brand-images/{id}. Ruten hedder
                                                         /brand-images/logos/{fil}, så hvert eneste kald
                                                         svarede 404 og faldt tilbage til mærkenavnet.
                                                         BrandStrip og BrandDisplay bruger logo-stien. -->
                                                    <img v-if="!brandImageFailed && page.props.product.brand.logo" :src="`/brand-images/${page.props.product.brand.logo}`" :alt="page.props.product.brand.name" class="brand-logo" @error="brandImageFailed = true" />
                                                    <span v-else class="brand-name-fallback">{{ page.props.product.brand.name }}</span>
                                                </Link>
                                            </div>
                                            <p v-if="productdata.attribute_texts && productdata.attribute_texts.length > 0"
                                               style="color: var(--pc-text-secondary); font-size: var(--pc-text-sm); margin-top: 4px;">
                                                {{ productdata.attribute_texts.join(' · ') }}
                                            </p>
                                            
                                            <!-- numberFormat() sætter selv "kr." på. Der stod " kr."
                                                 bagefter, så prisen læste "5.141,00 kr. kr." på 47 af 48
                                                 produktsider. -->
                                            <p class="text-muted mb-4" v-if="visningsnavn && harPriser">
                                                Sammenlign priser på {{ visningsnavn }} fra
                                                <strong>{{ numberFormat(productdata.prices.lowest_price_in_stock_including_shipping) }}</strong> til
                                                <strong>{{ numberFormat(productdata.prices.highest_price_including_shipping) }}</strong> hos
                                                <strong>{{ productdata.prices.total_retailers }}</strong>
                                                {{ productdata.prices.total_retailers === 1 ? 'forhandler' : 'forhandlere' }}.
                                            </p>

                                            <div class="price-section mb-4 p-3 bg-light rounded">
                                                <!--
                                                    "0,00 kr." er den hårdest mulige afvisning.

                                                    Siden viste <h3>0,00 kr.</h3> når ingen butik
                                                    havde varen. Den er korrekt sat til noindex,
                                                    men den havde alligevel 123 eksponeringer og 2
                                                    klik på 28 dage, hvilket gør den til en af de
                                                    bedst klikkede sider på hele sitet. De
                                                    besøgende mødte altså en pris på nul kroner,
                                                    og det er direkte tillidsskade.
                                                -->
                                                <div class="best-price" v-if="harPriser">
                                                    <h3 class="mb-0 text-primary">{{ numberFormat(productdata.prices.lowest_price_in_stock_including_shipping) }}</h3>
                                                    <span class="text-muted small">INKL. FRAGT</span>
                                                </div>
                                                <!--
                                                    Butikkerne har varen, men ingen af dem paa lager.
                                                    "Ingen aktuelle priser" er rigtigt om lageret og
                                                    forkert om siden: vi kender prisen, den kan bare
                                                    ikke koebes. At skjule den ville gaa ud over den
                                                    besoegende der netop leder efter hvad varen koster.
                                                -->
                                                <div class="best-price" v-else-if="harUdsolgtePriser">
                                                    <h3 class="mb-0 text-muted">Udsolgt lige nu</h3>
                                                    <span class="text-muted small">
                                                        Sidst set til {{ numberFormat(productdata.prices.lowest_price_including_shipping) }}
                                                        hos {{ productdata.prices.total_retailers }}
                                                        {{ productdata.prices.total_retailers === 1 ? 'forhandler' : 'forhandlere' }}.
                                                        Sæt en prisalarm, så siger vi til når den er på lager igen.
                                                    </span>
                                                </div>
                                                <div class="best-price" v-else>
                                                    <h3 class="mb-0 text-muted">Ingen aktuelle priser</h3>
                                                    <span class="text-muted small">Ingen butik har varen lige nu. Sæt en prisalarm, så siger vi til når den kommer tilbage.</span>
                                                </div>
                                                <!--
                                                    Kun naar der ER en pris paa lager. Ellers stod den
                                                    samme pris to gange: en gang som "sidst set" lige
                                                    over og en gang som "laveste pris" her.
                                                -->
                                                <p v-if="harPriser && productdata.prices && productdata.prices.lowest_price_including_shipping"
                                                   style="color: var(--pc-text-muted); font-size: var(--pc-text-xs); margin-top: 4px;">
                                                    Laveste pris inkl. fragt: {{ numberFormat(productdata.prices.lowest_price_including_shipping) }}
                                                    <template v-if="productdata.prices.total_retailers"> · {{ productdata.prices.total_retailers }} forhandlere</template>
                                                </p>
                                            </div>

                                            <div class="actions-row d-flex gap-2 align-items-center flex-wrap">
                                                <a v-if="urlForCheapestStore"
                                                   :href="`/redirect/${page.props.product.pricecompare_id}/${urlForCheapestStore}`"
                                                   target="_blank"
                                                   rel="sponsored noopener nofollow"
                                                   class="btn btn-pricecompare-primary btn-sm btn-cheapest">
                                                    <font-awesome-icon :icon="['fas', 'shop']" class="me-1" />
                                                    Gå til billigste butik
                                                </a>
                                                
                                                <PriceAlertButton 
                                                    :has-alert="!!page.props.userPriceAlert"
                                                    @open-modal="openPriceAlertModal"
                                                />
                                                
                                                <FavoriteButton
                                                    :product-id="page.props.product.pricecompare_id"
                                                    :initial-favorite-status="userFavoriteStatus"
                                                    :is-authenticated="isAuthenticated"
                                                    @favorite-changed="handleFavoriteChanged"
                                                />
                                                
                                                <AddToListButton
                                                    :product-id="page.props.product.pricecompare_id"
                                                    :is-authenticated="isAuthenticated"
                                                />
                                                
                                                <PriceAlertModal
                                                    :show="showAlertModal.value"
                                                    :product-id="page.props.product.pricecompare_id"
                                                    :is-authenticated="isAuthenticated"
                                                    :existing-alert="userPriceAlert"
                                                    @close="showAlertModal.value = false"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row design-row">
                            <div class="col-md-12">
                                <ul class="nav nav-tabs">
                                    <li class="nav-item">
                                        <a class="nav-link active" data-bs-toggle="tab" href="#priser">Priser</a>
                                    </li>
                                    <li class="nav-item">
                                        <a class="nav-link" data-bs-toggle="tab" href="#refurb-priser">Andre udgaver</a>
                                    </li>
                                </ul>
                                <div class="tab-content" id="productTabsContent">
                                    <div class="tab-pane fade show active" id="priser">
                                        <!--
                                            Reklameidentifikation. Markedsføringsloven kræver at det
                                            kommercielle kan genkendes; at siden er kommerciel fremgår
                                            af sammenhængen ("Gå til butik"), men at VI tjener på
                                            klikket gør ikke. Linjen står hvor linkene er, ikke i en
                                            FAQ-harmonika, forbrugeren skal kunne se det før han
                                            handler. Sorteringen er ren pris (orderBy
                                            price_with_shipping_min), så der er ingen betalt placering
                                            at oplyse om, og det siger vi eksplicit.
                                        -->
                                        <!--
                                            h2 over pristabellen.

                                            Siden havde INGEN h2 overhovedet. Tolv h5 og h6 fra
                                            megamenuen kom før h1, og derefter kom der ingen
                                            overskrifter. Sprogmodeller opdeler sider i afsnit ud
                                            fra overskrifter, så hele produktsiden var ét udelt
                                            tekstblok: modellen kunne ikke se hvor prislisten
                                            begyndte og specifikationerne sluttede. Det samme
                                            gælder Googles passage ranking. Med 38 % af trafikken
                                            fra AI-assistenter er det her det gør mest ondt.
                                        -->
                                        <h2 class="h5 mb-2" v-if="harPriser">
                                            Priser på {{ visningsnavn }} hos {{ page.props.product.prices.total_retailers }}
                                            {{ page.props.product.prices.total_retailers === 1 ? 'forhandler' : 'forhandlere' }}
                                        </h2>
                                        <p class="small text-muted mb-2">
                                            <strong>Annoncelinks.</strong> Vi får provision når du klikker videre til en butik og handler.
                                            Det ændrer ikke din pris, og det påvirker ikke rækkefølgen &mdash; listen er sorteret efter
                                            laveste pris inkl. fragt.
                                        </p>
                                        <div class="card">
                                            <div class="card-body p-0">
                                                <template v-for="(price, index) in page.props.productPrices" :key="index">
                                                    <PriceRow 
                                                        :price="price" 
                                                        :index="index" 
                                                        :productId="String(page.props.product.pricecompare_id)" 
                                                        class="mb-3"
                                                    />
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                    <!--
                                        Refurbished, brugt, demo og uoriginale kopivarer står
                                        HER og kun her. De indgår aldrig i pristabellen, i
                                        "billigste butik", i søgning, i sortering eller i
                                        prisalarmer. En refurbished iPhone til 500 kr ved siden
                                        af en ny til 8.000, eller en kopipatron til 399 kr ved
                                        siden af en original til 1.146, er ikke et godt tilbud.
                                        Det er en anden vare.
                                    -->
                                    <div class="tab-pane fade" id="refurb-priser">
                                        <div class="card">
                                            <div class="card-body" :class="{ 'p-0': (page.props.refurbishedPrices || []).length }">
                                                <template v-if="(page.props.refurbishedPrices || []).length">
                                                    <p class="small text-muted px-3 pt-3 mb-2">
                                                        Brugte, refurbishede og demo-modeller samt uoriginale
                                                        kopivarer. De indgår <strong>ikke</strong> i produktets pris
                                                        eller i sammenligningen ovenfor. Det er andre varer med en
                                                        anden stand, garanti eller producent.
                                                    </p>
                                                    <table class="table table-sm mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Butik</th><th>Stand</th><th class="text-end">Pris</th><th class="text-end">Inkl. fragt</th><th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(r, i) in page.props.refurbishedPrices" :key="i">
                                                                <td>{{ r.retailer?.name || 'Forhandler' }}</td>
                                                                <td><span class="badge bg-secondary">{{ r.tilstand }}</span></td>
                                                                <td class="text-end">{{ numberFormat(r.price) }}</td>
                                                                <td class="text-end">{{ numberFormat(r.price_with_shipping_min) }}</td>
                                                                <td class="text-end">
                                                                    <a
                                                                        :href="`/redirect/${page.props.product.pricecompare_id}/${r.price_guid}`"
                                                                        target="_blank"
                                                                        rel="sponsored noopener nofollow"
                                                                        class="btn btn-sm btn-outline-secondary"
                                                                    >Gå til butik</a>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </template>
                                                <template v-else>
                                                    Ingen andre udgaver af dette produkt fundet.
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <br/>
                        <div>
                            <div class="card">
                                <div class="card-body">
                                    <!--
                                        Prisudviklingen i ord, server-renderet.

                                        Grafen hentes klient-side, så de 10,5 millioner
                                        målinger vi har var usynlige for både Google og
                                        enhver sprogmodel. "Er den på tilbud" og "hvad har
                                        den kostet" er præcis det folk spørger en assistent
                                        om, og ChatGPT står allerede for 32 af vores 84
                                        målte sessioner. Teksten står FØR grafen, så den
                                        også er der når JavaScript ikke er kørt.
                                    -->
                                    <h2 v-if="prisudvikling" class="h5 mb-2">Prisudvikling</h2>
                                    <p v-if="prisudvikling" class="text-muted mb-3 prisudvikling-tekst">{{ prisudvikling.tekst }}</p>

                                    <PriceGraph :options="chartOptions" :productId="page.props.product.pricecompare_id" />
                                </div>
                            </div>
                        </div>
                        <br/>
                        <!--
                            BESKRIVELSEN LAA BAG ET KLIK, og ingen klikkede.

                            Den stod i fanen "Produktinfo", som ikke var den
                            aktive: `tab-pane fade` uden `show active`. Alle
                            139.890 beskrivelser var dermed usynlige for et
                            menneske, og Google vaegter indhold bag en
                            interaktion lavere. Vi har brugt maaneder paa at
                            skrive tekst ingen saa.

                            Den staar nu foer specifikationerne, altid synlig,
                            og fanerne er vaek: der var kun to, og den ene var
                            den her.
                        -->
                        <div v-if="page.props.product.product_description" class="row data-row">
                            <div class="col-md-12">
                                <div class="card">
                                    <div class="card-body">
                                        <h2 class="h5 mb-3">Om {{ visningsnavn }}</h2>
                                        <div class="produkttekst" v-html="sanitizeWithBreaks(page.props.product.product_description)"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row data-row">
                            <div class="col-md-12">
                                <div>
                                    <div>
                                        <div class="card">
                                            <div class="card-body">
                                                <h2 class="h5 mb-3">Specifikationer for {{ visningsnavn }}</h2>
                                                <div class="specs-container">
                                                    <template v-for="(group, gIdx) in page.props.product.attributes" :key="gIdx">
                                                        <h3 class="specs-group-header">{{ group.group_name }}</h3>
                                                        <table class="table table-sm specs-table mb-4">
                                                            <tbody>
                                                                <tr v-for="(specification, sIdx) in group.attributes" :key="sIdx">
                                                                    <td class="specs-label">{{ specification.name }}</td>
                                                                    <!--
                                                                        Læs det læsbare felt først. Når basen er
                                                                        normaliseret til rå tal, ER specification.value
                                                                        et tal, og så står der 268435456000 hvor der
                                                                        skulle stå 250 GB.
                                                                    -->
                                                                    <td class="specs-value">{{ specVaerdi(specification) }}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            &nbsp;
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-2">
                    <div class="rightmenu-wrapper">
                        <SidebarRelaterede :varer="relateredeMedBillede" :artikler="nyheder" />
                    </div>
                </div>
            </div>
        </div>
        <!--
            Videre herfra.
    
            77.228 produktsider havde nul udgående links til andre produkter.
            Ingen link-flow, så Google forstår ingen produktrelationer, og den
            bruger der ikke vil have netop denne vare, forlader sitet i stedet
            for at se en anden. ItemList-schemaet på kategorisiderne har 20
            produkt-URLs, men de er ikke rigtige links.
        -->
        <!--
            SAMME GITTER SOM RESTEN AF SIDEN.
            
            De to blokke laa i deres egen .container og blev derfor centreret
            over hele bredden, mens alt ovenfor staar i 10/2: en bred venstre
            kolonne og en smal hoejre. Det brød linjen midt paa siden.
        -->
        <div class="container-fluid product-detail-wrapper">
            <div class="row p-0">
                <div class="col-sm-12 col-md-10">
                    <div class="container-fluid">
                        <!--
                            Produktsiden er en del i PC-byggeren. Det stod der ikke foer.

                            3.244 af vores produktsider ER dele, med 10.856 eksponeringer
                            paa seks uger. Byggeren fik 11 og nul klik, fordi linket kun
                            laa i topmenuen, i footeren og paa de 27 spilsider.

                            Staar foer "Se ogsaa", saa det er det naeste skridt man ser
                            naar man er faerdig med varen — ikke efter atten andre varer.
                        -->
                        <Komponentbaand v-if="komponentbaand" :baand="komponentbaand" class="mb-4" />

                        <SeOgsaa :varer="seOgsaa" />
                    </div>
                </div>
                <div class="col-sm-12 col-md-2"></div>
            </div>
        </div>

    </ProductPageLayout>
</template>


<style scoped>
/*
 * Hovedbilledet i en kvadratisk kasse med object-fit: contain.
 *
 * Pladsen er reserveret foer billedet er hentet — ingen layout-hop — og
 * billedet beholder sit eget format i stedet for at blive strakt.
 */
.galleri-hoved {
    display: block;
    aspect-ratio: 1 / 1;
    background: #fff;
    border-radius: var(--pc-radius);
    overflow: hidden;
}

.galleri-hoved picture,
.galleri-hoved img {
    width: 100%;
    height: 100%;
    display: block;
}

.galleri-hoved img {
    object-fit: contain;
}

.galleri-strib {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    /* Ingen wrap: striben er EEN raekke, og pilene styrer resten. */
    flex-wrap: nowrap;
}

.galleri-pil {
    flex: 0 0 auto;
    width: 24px;
    height: 48px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--pc-bg-tertiary);
    border: 1px solid var(--pc-border, rgba(255, 255, 255, 0.12));
    border-radius: var(--pc-radius);
    color: var(--pc-text-primary);
    cursor: pointer;
    line-height: 1;
}

.galleri-pil:disabled {
    opacity: 0.35;
    cursor: default;
}

.galleri-pil:focus-visible {
    outline: 2px solid var(--pc-accent);
    outline-offset: 2px;
}

/*
 * Miniaturerne maa KRYMPE, ikke rage ud.
 *
 * Striben er fem miniaturer paa 48 px plus to pile paa 24 og seks
 * mellemrum: 321,6 px i alt. Paa en 360 px-skaerm har den 278 px at staa
 * paa, og med `flex: 0 0 auto` gjorde den det alligevel — den hoejre pil
 * endte 3 px uden for viewporten og gav hele produktsiden vandret rul.
 *
 * `flex: 0 1 48px` lader dem falde til det der er plads til, og gulvet paa
 * 32 px holder dem over WCAG 2.5.8's 24 px. Billedet indeni er
 * `object-fit: contain`, saa en smallere kasse beskaerer ingenting.
 */
.galleri-knap {
    flex: 0 1 48px;
    min-width: 32px;
    width: 48px;
    height: 48px;
    padding: 0;
    background: #fff;
    border: 2px solid transparent;
    border-radius: var(--pc-radius);
    overflow: hidden;
    cursor: pointer;
    line-height: 0;
}

.galleri-knap img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

/*
 * Det valgte billede skal kunne SES som valgt, ogsaa uden farve. Rammen
 * baerer det alene; en ren farveforskel ville forsvinde for den der ikke
 * skelner dem.
 */
.galleri-knap.valgt {
    border-color: var(--pc-accent);
}

.galleri-knap:focus-visible {
    outline: 2px solid var(--pc-accent);
    outline-offset: 2px;
}

.product-detail {
    display: flex;
    align-items: center;
}

.product-image {
    flex: 1;
}

.product-info {
    flex: 1;
}

.modern-product-card {
    border-radius: var(--pc-radius);
}

.product-title {
    font-family: var(--pc-font);
    font-weight: 600;
    color: var(--pc-text-primary);
}

.price-section {
    border-left: 4px solid var(--pc-accent);
    background-color: var(--pc-bg-tertiary);
    border-radius: var(--pc-radius);
}

.best-price {
    display: flex;
    flex-direction: column;
}

.best-price h3 {
    color: var(--pc-price);
    font-weight: 700;
    font-size: 1rem;
}

.actions-row {
    margin-top: 1.5rem;
}

.design-row {
    margin-top: 1rem;
}

.secondary-row {
    margin-top: 1rem;
}

.nav-tabs {
    border-bottom: none;
    background-color: transparent;
    border-radius: var(--pc-radius) var(--pc-radius) 0 0;
    overflow: hidden;
    justify-content: flex-start;
}

.nav-tabs .nav-item {
    text-align: center;
    margin-right: 0.5rem;
}

.nav-tabs .nav-link {
    border: none;
    border-radius: var(--pc-radius) var(--pc-radius) 0 0;
    color: var(--pc-text-secondary);
    font-family: var(--pc-font);
    font-weight: 500;
    padding: 0.5rem 1rem;
    background-color: var(--pc-bg-tertiary);
    transition: background-color var(--pc-transition);
    white-space: nowrap;
}

.nav-tabs .nav-link.active {
    background-color: var(--pc-bg-secondary);
    color: var(--pc-accent);
    font-weight: bold;
}

.nav-tabs .nav-link:hover {
    background-color: var(--pc-bg-hover);
}

.tab-content {
    border: 1px solid var(--pc-border);
    border-top: none;
    padding: 1rem;
    background-color: var(--pc-bg-secondary);
    border-radius: 0 0 var(--pc-radius) var(--pc-radius);
}

.rightmenu-wrapper {
    height: 100%;

    /*
     * Samme afstand foroven som venstre kolonnes foerste kort.
     *
     * Venstre side faar sin fra .design-row { margin-top: 1rem }. Hoejre side
     * havde ingen, saa "Relaterede varer" begyndte en centimeter hoejere end
     * galleriet og info-kortet ved siden af, og de tre kasser flugtede ikke.
     */
    margin-top: 1rem;
}

.product-detail-wrapper {
    height: 100%;
}

.price-label span {
    font-size: 0.8em;
    color: var(--pc-text-secondary);
}

.breadcrumb-wrapper {
    padding-left: 1.5rem;
    height: auto;
}

.breadcrumb-wrapper ol {
    padding: 0.1rem 0.1rem;
    margin-bottom: 0;
}

.breadcrumb-wrapper ol li a {
    text-decoration: none;
    color: var(--pc-accent);
    font-weight: bold;
    text-align: center;
    text-transform: uppercase;
    font-size: 0.8rem;
}

.expander-wrapper {
    height: 1.5rem;
}

.expander-wrapper:hover {
    cursor: pointer;
}

.favorite-animation {
    animation: heartBeat 0.7s ease-in-out;
    transform-origin: center;
}

@keyframes heartBeat {
    0% {
        transform: scale(1);
    }
    14% {
        transform: scale(1.3);
    }
    28% {
        transform: scale(1);
    }
    42% {
        transform: scale(1.3);
    }
    70% {
        transform: scale(1);
    }
}

.compact-price-row {
    padding: 0.5rem;
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-sm);
}

.retailer-logo img {
    width: 40px;
    height: 40px;
}

.stock-info {
    font-size: 0.9rem;
}

.lead-time {
    font-size: 0.8rem;
    color: var(--pc-text-secondary);
}

.price-info {
    text-align: right;
}

.price-info h5 {
    font-size: 1rem;
    margin-bottom: 0;
}

.action-link .btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
}

.brand-logo {
    max-height: 30px;
    object-fit: contain;
}

.card {
    background-color: var(--pc-bg-secondary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-lg);
    box-shadow: var(--pc-shadow-sm);
}

.card.shadow-sm {
    box-shadow: var(--pc-shadow);
}

:deep(.bg-light) {
    background-color: var(--pc-bg-tertiary) !important;
}

:deep(.text-primary) {
    color: var(--pc-price) !important;
}

:deep(.text-muted) {
    color: var(--pc-text-secondary) !important;
}

:deep(.bg-secondary) {
    background-color: var(--pc-bg-tertiary) !important;
    color: var(--pc-text-primary) !important;
}

:deep(.border-bottom) {
    border-bottom-color: var(--pc-border) !important;
}

:deep(.d-flex.flex-column .d-flex:nth-child(even)) {
    background-color: var(--pc-bg-tertiary);
}

@media (max-width: 576px) {
    .headers {
        display: none;
    }

    .tab-content {
        padding: 0.5rem;
    }

    .tab-pane {
        padding: 0.5rem;
    }

    .card {
        margin-bottom: 1rem;
    }

    .table {
        font-size: 0.8rem;
    }

    .table th,
    .table td {
        padding: 0.5rem;
    }

    .nav-tabs {
        flex-wrap: wrap;
    }

    .nav-tabs .nav-item {
        flex: 1 1 50%;
        text-align: center;
    }

    .nav-tabs .nav-link {
        font-size: 0.9rem;
        padding: 0.5rem;
    }

    .tab-content {
        font-size: 0.9rem;
    }
}

.specs-group-header {
    font-size: 0.95rem;
    font-weight: 700;
    padding: 8px 12px;
    margin-bottom: 0;
    background: var(--pc-bg-secondary, #f8f9fa);
    border-radius: 6px 6px 0 0;
    border-bottom: 2px solid var(--pc-primary, #00c9a7);
}

/*
 * Kolonnebredderne skal HOLDE.
 *
 * `.specs-label` har staaet med `width: 40%` (45 paa mobil) hele tiden, men
 * med `table-layout: auto` er en bredde kun et forslag: browseren giver
 * kolonnen det indholdet beder om. Et etiketnavn som "Producentens
 * varenummer" er laengere end sin vaerdi, saa maalt paa en 390 px-telefon
 * 15/9 2026 fik etiketten 207 px og selve vaerdien 67 — og
 * "SM-A256BZKHEUB" blev braekket over fire linjer i en kolonne paa en
 * tredjedel af etikettens.
 *
 * Det er vendt om af hvad man kom efter. `fixed` goer bredden bindende.
 */
.specs-table {
    margin-top: 0;
    table-layout: fixed;
    width: 100%;
}

.specs-table td {
    padding: 8px 12px;
    vertical-align: top;
}

.specs-label {
    color: var(--pc-text-muted, #6c757d);
    width: 40%;
    font-size: 0.875rem;
    /* Med fixed bredde kan et langt etiketnavn ikke skubbe kolonnen laengere
       ud — saa skal det brydes i stedet for at flyde over. */
    word-break: break-word;
}

.specs-value {
    font-weight: 500;
    font-size: 0.875rem;
    word-break: break-word;
}

.btn-cheapest {
    font-size: 0.875rem;
    padding: 0.4rem 1rem;
}

@media (max-width: 575.98px) {
    .specs-label {
        width: 45%;
        font-size: 0.8rem;
        padding: 6px 8px;
    }

    .specs-value {
        font-size: 0.8rem;
        padding: 6px 8px;
    }

    .specs-group-header {
        font-size: 0.85rem;
        padding: 6px 8px;
    }

    .btn-cheapest {
        font-size: 0.8rem;
        padding: 0.35rem 0.75rem;
    }

    .actions-row {
        gap: 0.4rem !important;
    }
}
</style>